import useCompleteGoal from '@/hooks/growthzone/useCompleteGoal';
import useEditGoal from '@/hooks/growthzone/useEditGoal';
import { background, extra, primary, secondary, text } from '@/theme/colors/palettes';
import { TDomainGoal, TDomainGrowthZone } from '@/types/GrowthZone.types';
import { Center, HStack, Input, Text, Tooltip, VStack } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import DomainGoalsHeader from './DomainGoalsHeader';
import LogoSpinner from '@/components/atoms/LogoSpinner';
import { MAX_GOALS_PER_DOMAIN } from '@/constants/defaultValues';
import SvgIcon from '@/components/atoms/SvgIcon';
import svgIcons from '@/assets/svg/icons';
import MYOCheckbox from '@/components/atoms/MYOCheckbox';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import GoalCompletionConfirmationModal from './GoalCompletionConfirmationModal';
import { DOMAIN_NEW_GOAL_PLACEHOLDERS } from '@/constants/stringVars';
import GrowthZoneCreate from './GrowthZoneCreate';
import { MAX_CHARS_IN_GOAL, MIN_CHARS_IN_GOAL } from '@/constants';

export const GOAL_HEIGHT = 53;

const CHARS_TO_SHOW_TOOLTIP = 38;

const schema = yup
  .object()
  .shape({
    goal: yup
      .string()
      .required('Goal is required.')
      .min(MIN_CHARS_IN_GOAL, `A goal must have at least ${MIN_CHARS_IN_GOAL} characters.`)
      .max(MAX_CHARS_IN_GOAL, `A goal can have a maximum of ${MAX_CHARS_IN_GOAL} characters.`),
  })
  .required();

interface Props {
  domainGoalData: TDomainGrowthZone;
  refetchAllGoals: () => void;
  isRefetchingAllGoals: boolean;
  headerHidden?: boolean;
  showMultipleCreateItems?: boolean;
}

const GrowthZoneDomainItem = ({
  domainGoalData,
  refetchAllGoals,
  isRefetchingAllGoals,
  headerHidden,
  showMultipleCreateItems = false,
}: Props) => {
  // a UX-helper variable which is set to true on new goal submit; and set to false when refetch is done
  const [hasJustAddedAGoalIndex, setHasJustAddedAGoalIndex] = useState(-1);
  const [hasJustCompletedAGoal, setHasJustCompletedAGoal] = useState(false);
  const [hasJustEditedAGoal, setHasJustEditedAGoal] = useState(false);

  const totalGoalsMissingOutOfMaxGoals = MAX_GOALS_PER_DOMAIN - domainGoalData.goals.length;
  const missingGoalsToMax = showMultipleCreateItems
    ? DOMAIN_NEW_GOAL_PLACEHOLDERS[domainGoalData.domain].slice(0, totalGoalsMissingOutOfMaxGoals)
    : totalGoalsMissingOutOfMaxGoals >= 1
      ? [DOMAIN_NEW_GOAL_PLACEHOLDERS[domainGoalData.domain][0]]
      : [];

  const { mutate: completeGoal, isPending: isCompleteGoalPending } = useCompleteGoal(domainGoalData.domain, () => {
    refetchAllGoals();
  });

  // after the refetch is done, we reset all UX helper loading states
  useEffect(() => {
    if (!isRefetchingAllGoals) {
      setHasJustAddedAGoalIndex(-1);

      setHasJustCompletedAGoal(false);
      setCheckedGoal(undefined);

      setHasJustEditedAGoal(false);
      setGoalToEdit(undefined);
    }
  }, [isRefetchingAllGoals]);

  // Completing a goal
  const [checkedGoal, setCheckedGoal] = useState<TDomainGoal | undefined>();
  const [isGoalCompletionModalOpen, setIsGoalCompletionModalOpen] = useState(false);

  const markCheckedGoalAsComplete = useCallback(() => {
    completeGoal(checkedGoal!.id);

    setHasJustCompletedAGoal(true);

    setIsGoalCompletionModalOpen(false);
  }, [checkedGoal]);

  // Editing a goal
  const {
    reset: resetEditForm,
    setValue: setEditValue,
    watch: watchEdit,
    register: registerEdit,
  } = useForm<{ goal: string }>({ resolver: yupResolver(schema), mode: 'onChange', defaultValues: { goal: '' } });

  const editGoalValue = watchEdit('goal');

  const { mutate: editGoal, isPending: isEditGoalPending } = useEditGoal(domainGoalData.domain, () => {
    resetEditForm();

    refetchAllGoals();
  });

  const [goalToEdit, setGoalToEdit] = useState<TDomainGoal | undefined>();

  const onPressGoalToEdit = (goal: TDomainGoal) => {
    setGoalToEdit(goal);
    setEditValue('goal', goal.text);
  };

  const onSubmitEditDomainGoal = () => {
    setHasJustEditedAGoal(true);
    editGoal({ goalId: goalToEdit!.id, editedDomainGoalText: editGoalValue });
  };

  const stopGoalEdit = () => {
    setGoalToEdit(undefined);
  };

  return (
    <VStack
      width={'100%'}
      backgroundColor={extra.white}
      gap={4}
      justify={'space-between'}
      alignItems={'center'}
      rounded={10}
      style={{
        boxShadow: headerHidden ? 'none' : '0px 4px 12px 0px #7C9EBC40',
        padding: headerHidden ? 0 : 16,
      }}
    >
      {/* Domain Goals Header */}
      {!headerHidden && <DomainGoalsHeader domainGoalData={domainGoalData} />}

      {domainGoalData.goals.map((goal) => {
        const isChecked = checkedGoal?.id === goal.id;
        const isCheckedGoalBeingCompleted =
          goal.id === checkedGoal?.id && (isCompleteGoalPending || hasJustCompletedAGoal);

        const isBeingEdited = goal.id === goalToEdit?.id;
        const editSubmitDisabled =
          editGoalValue?.length < MIN_CHARS_IN_GOAL || editGoalValue?.length > MAX_CHARS_IN_GOAL;
        const isEditedGoalBeingSubmitted = goal.id === goalToEdit?.id && (isEditGoalPending || hasJustEditedAGoal);

        if (isCheckedGoalBeingCompleted || isEditedGoalBeingSubmitted) {
          return (
            <Center paddingY={'10px'} key={goal.id}>
              <LogoSpinner isLoading size={`${GOAL_HEIGHT - 20}px`} />
            </Center>
          );
        } else {
          return (
            <Tooltip
              key={goal.id}
              visibility={goal.text.length > CHARS_TO_SHOW_TOOLTIP ? 'visible' : 'hidden'}
              label={goal.text}
              padding={'10px 16px'}
              backgroundColor={'extra.white'}
              textColor={'text.mediumBlue'}
              maxWidth={'400px'}
              rounded={'6px'}
              boxShadow="0px 4px 20px 0px #00417926"
            >
              <HStack
                key={goal.id}
                flex={1}
                width={'100%'}
                padding={'10px 16px'}
                justify="space-between"
                alignItems={'start'}
                rounded={'6px'}
                minHeight={GOAL_HEIGHT}
                backgroundColor={isBeingEdited ? extra.white : background.lightPurple}
                boxShadow={isBeingEdited ? '0px 2px 8px 0px #00417926' : '0px 2px 4px 0px #0041790D'}
                borderColor={primary[500]}
                borderWidth={isBeingEdited ? 1 : 0}
              >
                {isBeingEdited ? (
                  <Input
                    autoFocus
                    {...registerEdit('goal')}
                    maxLength={MAX_CHARS_IN_GOAL}
                    value={editGoalValue}
                    onChange={(e) => setEditValue('goal', e.target.value, { shouldValidate: true })}
                    borderWidth={0}
                    padding={'1px'}
                    maxHeight={'28px'}
                    focusBorderColor={'transparent'}
                    placeholder={DOMAIN_NEW_GOAL_PLACEHOLDERS[domainGoalData.domain][0]}
                    color={'text.mediumBlue'}
                    fontWeight={600}
                    fontSize={14}
                    width={'90%'}
                    _placeholder={{ fontWeight: '600', color: 'text.mediumGray', fontSize: 14, maxLines: 1 }}
                    onClickCapture={(e) => e.stopPropagation()}
                    onBlur={stopGoalEdit}
                    onKeyDown={(e) => {
                      if (e.code === 'Escape') {
                        stopGoalEdit();
                      }
                    }}
                  />
                ) : (
                  <VStack
                    width={'88%'}
                    gap={0.5}
                    alignItems={'start'}
                    cursor={'pointer'}
                    onClick={() => onPressGoalToEdit(goal)}
                  >
                    <Text variant={'urbanistExtraBoldSmall'} color={text.mediumGray}>
                      GOAL
                    </Text>
                    <Text
                      variant="urbanistSemiBold"
                      fontSize={14}
                      color={text.mediumBlue}
                      noOfLines={1}
                      textAlign={'start'}
                    >
                      {goal.text}
                    </Text>
                  </VStack>
                )}

                {isBeingEdited ? (
                  <Center
                    padding={'6px'}
                    rounded={'5px'}
                    style={{
                      backgroundColor: editSubmitDisabled ? secondary[300] : secondary[500],
                      boxShadow: '0px 2px 4px 0px #0041790D',
                    }}
                    cursor={editSubmitDisabled ? 'not-allowed' : 'pointer'}
                    onMouseDown={
                      editSubmitDisabled
                        ? (e) => {
                            e.stopPropagation();
                            e.preventDefault();
                          }
                        : onSubmitEditDomainGoal
                    }
                  >
                    <SvgIcon
                      iconPath={svgIcons.circledArrowUp}
                      color={extra.white}
                      additionalSvgProps={{ fillRule: 'evenodd', clipRule: 'evenodd' }}
                      size={16}
                      boxWidth={20}
                      boxHeight={20}
                    />
                  </Center>
                ) : (
                  <MYOCheckbox
                    checked={isChecked}
                    onClick={() => {
                      setCheckedGoal(goal);
                      setIsGoalCompletionModalOpen(true);
                    }}
                  />
                )}
              </HStack>
            </Tooltip>
          );
        }
      })}

      {missingGoalsToMax.map((missingPlaceholder, i) => (
        <GrowthZoneCreate
          key={missingPlaceholder}
          refetchAllGoals={refetchAllGoals}
          placeholder={missingPlaceholder}
          domain={domainGoalData.domain}
          hasJustAddedAGoalIndex={hasJustAddedAGoalIndex}
          setHasJustAddedAGoalIndex={setHasJustAddedAGoalIndex}
          index={i}
        />
      ))}

      <GoalCompletionConfirmationModal
        isOpen={isGoalCompletionModalOpen}
        onClose={() => {
          setIsGoalCompletionModalOpen(false);
          setCheckedGoal(undefined);
        }}
        onSubmit={markCheckedGoalAsComplete}
        isLoading={isCompleteGoalPending}
      />
    </VStack>
  );
};

export default GrowthZoneDomainItem;
